import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiClock, FiPlus } from 'shared/ui/fiIcons';
import { useI18n } from '../../context/I18nContext';
import { useAppSelector } from '../../store/hooks';
import { type CalendarSpace, ensureCalendarSpaceOnline, getCalendarSpaceOnline } from './calendarStorage';
import { CalendarTaskModal } from './components/CalendarTaskModal';
import {
  type CalendarTask,
  type CalendarTaskOccurrence,
  type CalendarTaskLabel,
  buildCalendarTaskDraftFromCard,
  completeCalendarTaskOccurrenceOnline,
  getCalendarLabelsOnline,
  getCalendarTaskOccurrencesOnline,
  getCalendarTasksOnline,
  getCalendarTaskSeriesId,
  moveCalendarTaskOccurrenceOnline,
  resizeCalendarTaskOccurrenceOnline,
} from './calendarTaskStorage';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_HOURS = Array.from({ length: 24 }, (_, index) => index);
const DAY_START_HOUR = DAY_HOURS[0];
const DAY_ROW_HEIGHT = 72;
type CalendarViewMode = 'month' | 'week' | 'day';

type CalendarDay = {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
};

const buildMonthDays = (referenceDate: Date): CalendarDay[] => {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  const today = new Date();

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);

    return {
      date,
      isCurrentMonth: date.getMonth() === month,
      isToday:
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate(),
    };
  });
};

const isSameDate = (left: Date, right: Date) => (
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()
);

const getSlotDate = (date: Date, hour: number) => {
  const next = new Date(date);
  next.setHours(hour, 0, 0, 0);
  return next;
};

const formatTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isTaskOverdue = (task: CalendarTask) => {
  if (task.isCompleted || !task.deadlineAt) return false;
  const deadline = new Date(task.deadlineAt);
  return !Number.isNaN(deadline.getTime()) && deadline.getTime() < Date.now();
};

const dayKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const CalendarBoardScreen: React.FC = () => {
  const { t, locale } = useI18n();
  const { calendarId } = useParams();
  const { user } = useAppSelector(state => state.auth);
  const calendarOwnerKey = useMemo(() => {
    if (user?.id) return `user-${user.id}`;
    return user?.email || user?.username || 'anonymous';
  }, [user?.email, user?.id, user?.username]);
  const [calendarSpace, setCalendarSpace] = React.useState<CalendarSpace | null>(null);
  const [isCalendarLoading, setIsCalendarLoading] = React.useState(true);
  const [viewMode, setViewMode] = React.useState<CalendarViewMode>('month');
  const [selectedDate, setSelectedDate] = React.useState(() => new Date());
  const [tasks, setTasks] = React.useState<CalendarTask[]>([]);
  const [occurrences, setOccurrences] = React.useState<CalendarTaskOccurrence[]>([]);
  const [labels, setLabels] = React.useState<CalendarTaskLabel[]>([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState<CalendarTask | null>(null);
  const [editingSeriesTaskId, setEditingSeriesTaskId] = React.useState<string | null>(null);
  const [editingOccurrenceStartAt, setEditingOccurrenceStartAt] = React.useState<string | null>(null);
  const [editingOccurrenceDate, setEditingOccurrenceDate] = React.useState<string | null>(null);
  const [taskDraft, setTaskDraft] = React.useState<Partial<CalendarTask> | null>(null);
  const [calendarNotice, setCalendarNotice] = React.useState('');
  const calendarDays = useMemo(() => buildMonthDays(selectedDate), [selectedDate]);
  const occurrenceRange = useMemo(() => ({
    start: dayKey(calendarDays[0].date),
    end: dayKey(calendarDays[calendarDays.length - 1].date),
  }), [calendarDays]);
  const monthTitle = useMemo(
    () => selectedDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
    [locale, selectedDate]
  );
  const weekDays = useMemo(() => {
    const start = new Date(selectedDate);
    const offset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - offset);

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [selectedDate]);
  const selectedDayTitle = useMemo(
    () => selectedDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
    [locale, selectedDate]
  );
  const labelById = useMemo(
    () => new Map(labels.map(label => [label.id, label])),
    [labels]
  );

  React.useEffect(() => {
    let isMounted = true;
    setIsCalendarLoading(true);

    const loadCalendarSpace = async () => {
      const nextCalendarSpace = calendarId
        ? await getCalendarSpaceOnline(calendarId)
        : await ensureCalendarSpaceOnline(t('calendar.defaultTitle'), calendarOwnerKey);
      if (!isMounted) return;
      setCalendarSpace(nextCalendarSpace);
      setIsCalendarLoading(false);
    };

    void loadCalendarSpace();
    return () => {
      isMounted = false;
    };
  }, [calendarId, calendarOwnerKey, t]);

  const loadCalendarData = React.useCallback(async () => {
    if (!calendarSpace) return;
    const [nextTasks, nextLabels, nextOccurrences] = await Promise.all([
      getCalendarTasksOnline(calendarSpace.id),
      getCalendarLabelsOnline(calendarSpace.id),
      getCalendarTaskOccurrencesOnline(calendarSpace.id, occurrenceRange.start, occurrenceRange.end),
    ]);
    setTasks(nextTasks);
    setLabels(nextLabels);
    setOccurrences(nextOccurrences);
  }, [calendarSpace, occurrenceRange.end, occurrenceRange.start]);

  React.useEffect(() => {
    if (!calendarSpace) return;
    void loadCalendarData();
    const rawDraft = window.sessionStorage.getItem('boardly.calendar.taskDraft');
    if (rawDraft) {
      window.sessionStorage.removeItem('boardly.calendar.taskDraft');
      try {
        const parsed = JSON.parse(rawDraft);
        setTaskDraft(buildCalendarTaskDraftFromCard(parsed));
        setEditingTask(null);
        setIsTaskModalOpen(true);
      } catch {
        setTaskDraft(null);
      }
    }
  }, [calendarSpace, loadCalendarData]);

  React.useEffect(() => {
    if (!calendarNotice) return;
    const timeout = window.setTimeout(() => setCalendarNotice(''), 4200);
    return () => window.clearTimeout(timeout);
  }, [calendarNotice]);

  if (isCalendarLoading) {
    return <div className="loading-state">{t('common.loading')}</div>;
  }

  if (!calendarSpace) {
    return <Navigate to="/boards" replace />;
  }

  if (!calendarId) {
    return <Navigate to={`/calendar/${calendarSpace.id}`} replace />;
  }

  const handleSelectDate = (date: Date, nextViewMode: CalendarViewMode = viewMode) => {
    setSelectedDate(date);
    setViewMode(nextViewMode);
  };

  const openCreateTask = (date?: Date) => {
    const startAt = date ? date.toISOString() : undefined;
    setTaskDraft(startAt ? { startAt } : null);
    setEditingTask(null);
    setEditingOccurrenceStartAt(null);
    setEditingOccurrenceDate(null);
    setIsTaskModalOpen(true);
  };

  const openEditTask = (
    task: CalendarTask,
    occurrenceStartAt?: string,
    seriesTaskId?: string,
    occurrenceDate?: string
  ) => {
    const taskOccurrence = task as Partial<CalendarTaskOccurrence>;
    const parentTaskId = seriesTaskId || taskOccurrence.parentTaskId || getCalendarTaskSeriesId(task);
    const nextOccurrenceStartAt = occurrenceStartAt || task.startAt;
    setEditingTask(task);
    setEditingSeriesTaskId(parentTaskId);
    setEditingOccurrenceStartAt(nextOccurrenceStartAt);
    setEditingOccurrenceDate(occurrenceDate || taskOccurrence.occurrenceDate || dayKey(new Date(nextOccurrenceStartAt)));
    setTaskDraft(null);
    setIsTaskModalOpen(true);
  };

  const handleTaskSaved = async (_task: CalendarTask, usedAutoStart: boolean) => {
    await loadCalendarData();
    setIsTaskModalOpen(false);
    setEditingTask(null);
    setEditingSeriesTaskId(null);
    setEditingOccurrenceStartAt(null);
    setEditingOccurrenceDate(null);
    setTaskDraft(null);
    if (usedAutoStart) setCalendarNotice(t('calendar.task.autoStartNotice'));
  };

  const handleMoveTask = async (taskId: string, startAt: string, occurrenceDate?: string) => {
    const baseTaskId = getCalendarTaskSeriesId({ id: taskId });
    const occurrenceDateKey = occurrenceDate || dayKey(new Date(startAt));
    const result = await moveCalendarTaskOccurrenceOnline(calendarSpace.id, baseTaskId, occurrenceDateKey, startAt);
    if (result.collisionAdjusted) setCalendarNotice(t('calendar.task.collisionNotice'));
    await loadCalendarData();
  };

  const handleDragStart = (event: React.DragEvent, taskId: string, occurrenceDate?: string) => {
    event.dataTransfer.setData('text/plain', JSON.stringify({ taskId, occurrenceDate }));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDropOnDate = (event: React.DragEvent, date: Date, hour = 9) => {
    event.preventDefault();
    const rawPayload = event.dataTransfer.getData('text/plain');
    if (!rawPayload) return;
    try {
      const payload = JSON.parse(rawPayload);
      void handleMoveTask(
        payload.taskId,
        getSlotDate(date, hour).toISOString(),
        payload.occurrenceDate || (payload.occurrenceStartAt ? dayKey(new Date(payload.occurrenceStartAt)) : undefined)
      );
    } catch {
      void handleMoveTask(rawPayload, getSlotDate(date, hour).toISOString());
    }
  };

  const handleResizeTask = async (task: CalendarTaskOccurrence, edge: 'top' | 'bottom', deltaMinutes: number) => {
    const roundedDelta = Math.round(deltaMinutes / 15) * 15;
    if (roundedDelta === 0) return;
    const start = new Date(task.startAt);
    const nextStartAt = edge === 'top'
      ? new Date(start.getTime() + roundedDelta * 60000).toISOString()
      : task.startAt;
    const nextDuration = edge === 'top'
      ? Math.max(15, task.durationMinutes - roundedDelta)
      : Math.max(15, task.durationMinutes + roundedDelta);

    await resizeCalendarTaskOccurrenceOnline(calendarSpace.id, task.parentTaskId, task.occurrenceDate, nextStartAt, nextDuration);
    await loadCalendarData();
  };

  const startResize = (event: React.MouseEvent, task: CalendarTaskOccurrence, edge: 'top' | 'bottom') => {
    event.stopPropagation();
    event.preventDefault();
    const startY = event.clientY;
    const onMove = (moveEvent: MouseEvent) => moveEvent.preventDefault();
    const onUp = (upEvent: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      void handleResizeTask(task, edge, ((upEvent.clientY - startY) / DAY_ROW_HEIGHT) * 60);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const tasksForDate = (date: Date): CalendarTaskOccurrence[] => occurrences
    .filter(task => isSameDate(new Date(task.startAt), date))
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const tasksForSlot = (date: Date, hour: number) => tasksForDate(date)
    .filter(task => new Date(task.startAt).getHours() === hour);

  const toggleTaskCompletion = async (task: CalendarTaskOccurrence) => {
    await completeCalendarTaskOccurrenceOnline(calendarSpace.id, task.parentTaskId, task.occurrenceDate, !task.isCompleted);
    await loadCalendarData();
  };

  const getDayTaskStyle = (task: CalendarTaskOccurrence): React.CSSProperties => {
    const start = new Date(task.startAt);
    const minutesFromStart = Math.max(0, (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes());
    const dateTasks = tasksForDate(start);
    const overlappingTasks = dateTasks.filter(item => {
      const itemStart = new Date(item.startAt).getTime();
      const itemEnd = itemStart + item.durationMinutes * 60000;
      const taskStart = start.getTime();
      const taskEnd = taskStart + task.durationMinutes * 60000;
      return taskStart < itemEnd && taskEnd > itemStart;
    });
    const overlapIndex = Math.max(0, overlappingTasks.findIndex(item => item.occurrenceKey === task.occurrenceKey));
    const overlapCount = Math.max(1, overlappingTasks.length);
    const width = `calc((100% - ${(overlapCount - 1) * 8}px) / ${overlapCount})`;
    return {
      top: `${(minutesFromStart / 60) * DAY_ROW_HEIGHT}px`,
      height: `${Math.max(36, (task.durationMinutes / 60) * DAY_ROW_HEIGHT)}px`,
      left: `calc(${overlapIndex} * (${width} + 8px))`,
      width,
      ['--calendar-task-color' as string]: task.headerColor || '#2563eb',
    };
  };

  const renderTaskLabels = (task: CalendarTask) => {
    const taskLabels = task.labelIds.map(labelId => labelById.get(labelId)).filter(Boolean) as CalendarTaskLabel[];
    if (!taskLabels.length) return null;
    return (
      <div className="calendar-task-label-badges">
        {taskLabels.slice(0, 3).map(label => (
          <span key={label.id} style={{ background: label.color }}>{label.name}</span>
        ))}
      </div>
    );
  };

  const renderTaskChip = (task: CalendarTaskOccurrence, compact = false, draggableTask = true) => (
    <div
      key={task.occurrenceKey}
      className={`calendar-task-chip ${task.isCompleted ? 'completed' : ''} ${isTaskOverdue(task) ? 'overdue' : ''}`}
      draggable={draggableTask}
      onDragStart={(event) => {
        if (!draggableTask) return;
        handleDragStart(event, task.parentTaskId, task.occurrenceDate);
      }}
      onClick={(event) => {
        event.stopPropagation();
        openEditTask(task, task.startAt, task.parentTaskId, task.occurrenceDate);
      }}
      style={{ ['--calendar-task-color' as string]: task.headerColor || '#2563eb' }}
    >
      <div className="calendar-task-chip-main">
        <button
          type="button"
          className="calendar-task-complete"
          onClick={(event) => {
            event.stopPropagation();
            void toggleTaskCompletion(task);
          }}
          aria-label={task.isCompleted ? t('calendar.task.markIncomplete') : t('calendar.task.markComplete')}
        >
          {task.isCompleted ? '✓' : ''}
        </button>
        <span>{compact ? task.title : `${formatTime(task.startAt)} ${task.title}`}</span>
      </div>
      {renderTaskLabels(task)}
      {isTaskOverdue(task) && <strong>{t('calendar.task.overdue')}</strong>}
    </div>
  );

  const renderTimelineTask = (task: CalendarTaskOccurrence) => (
    <div
      key={task.occurrenceKey}
      className={`calendar-day-timeline-task ${task.isCompleted ? 'completed' : ''} ${isTaskOverdue(task) ? 'overdue' : ''}`}
      draggable
      onDragStart={(event) => handleDragStart(event, task.parentTaskId, task.occurrenceDate)}
      onClick={() => openEditTask(task, task.startAt, task.parentTaskId, task.occurrenceDate)}
      style={getDayTaskStyle(task)}
    >
      <button type="button" className="calendar-resize-handle top" onMouseDown={(event) => startResize(event, task, 'top')} aria-label={t('calendar.task.resizeStart')} />
      <div className="calendar-day-timeline-task-head">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void toggleTaskCompletion(task);
          }}
          aria-label={task.isCompleted ? t('calendar.task.markIncomplete') : t('calendar.task.markComplete')}
        >
          {task.isCompleted ? '✓' : ''}
        </button>
        <strong>{task.title}</strong>
      </div>
      <span>
        {formatTime(task.startAt)} · {Math.floor(task.durationMinutes / 60)}h {task.durationMinutes % 60}m
      </span>
      {renderTaskLabels(task)}
      {isTaskOverdue(task) && <em>{t('calendar.task.overdue')}</em>}
      <button type="button" className="calendar-resize-handle bottom" onMouseDown={(event) => startResize(event, task, 'bottom')} aria-label={t('calendar.task.resizeEnd')} />
    </div>
  );

  const completedCount = occurrences.filter(task => task.isCompleted).length;
  const taskById = new Map(tasks.map(task => [task.id, task]));
  const getRootTaskId = (taskId: string) => {
    const visited = new Set<string>();
    let currentId = taskId;
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const currentTask = taskById.get(currentId);
      if (!currentTask?.recurrenceParentId) return currentId;
      currentId = currentTask.recurrenceParentId;
    }
    return taskId;
  };
  const habitCount = new Set(
    occurrences
      .filter(task => task.repeatType !== 'none')
      .map(task => getRootTaskId(task.parentTaskId))
  ).size;

  return (
    <div className="calendar-board-page">
      <header className="calendar-board-header">
        <div className="calendar-board-header-main">
          <Link to="/boards" className="calendar-board-back" aria-label={t('calendar.backToBoards')}>
            <FiArrowLeft aria-hidden="true" />
          </Link>
          <div>
            <span className="calendar-board-kicker">{t('calendar.kicker')}</span>
            <h1>{calendarSpace.title}</h1>
          </div>
        </div>
        <button type="button" className="calendar-board-add" onClick={() => openCreateTask()}>
          <FiPlus aria-hidden="true" />
          <span>{t('calendar.addTask')}</span>
        </button>
        <Link to={`/calendar/${calendarSpace.id}/habits`} className="calendar-board-add calendar-board-habits">
          <FiCheckCircle aria-hidden="true" />
          <span>{t('habits.open')}</span>
        </Link>
      </header>

      {calendarNotice && <div className="board-top-toast">{calendarNotice}</div>}

      <main className="calendar-board-layout">
        <section className="calendar-board-workspace" aria-label={t('calendar.title')}>
          <div className="calendar-board-monthbar">
            <div>
              <p>{viewMode === 'day' ? t('calendar.day') : viewMode === 'week' ? t('calendar.week') : t('calendar.month')}</p>
              <h2>{viewMode === 'day' ? selectedDayTitle : monthTitle}</h2>
            </div>
            <div className="calendar-view-switcher" role="tablist" aria-label={t('calendar.viewSwitcher')}>
              {(['month', 'week', 'day'] as CalendarViewMode[]).map(mode => (
                <button
                  key={mode}
                  type="button"
                  className={viewMode === mode ? 'active' : ''}
                  onClick={() => setViewMode(mode)}
                >
                  {t(`calendar.view.${mode}`)}
                </button>
              ))}
            </div>
          </div>

          {viewMode === 'month' && (
            <div className="calendar-grid">
              {WEEK_DAYS.map(day => (
                <div key={day} className="calendar-weekday">
                  {t(`calendar.weekday.${day.toLowerCase()}`)}
                </div>
              ))}
              {calendarDays.map(day => (
                <div
                  role="button"
                  tabIndex={0}
                  key={day.date.toISOString()}
                  className={[
                    'calendar-day',
                    day.isCurrentMonth ? '' : 'muted',
                    day.isToday ? 'today' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleSelectDate(day.date, 'day')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') handleSelectDate(day.date, 'day');
                  }}
                >
                  <span>{day.date.getDate()}</span>
                  <div className="calendar-day-tasks">
                    {tasksForDate(day.date).slice(0, 3).map(task => renderTaskChip(task, true, false))}
                    {tasksForDate(day.date).length > 3 && (
                      <em>{t('calendar.task.more', { count: tasksForDate(day.date).length - 3 })}</em>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {viewMode === 'week' && (
            <div className="calendar-week-view">
              <div className="calendar-week-corner" />
              {weekDays.map(date => (
                <button
                  type="button"
                  key={date.toISOString()}
                  className="calendar-week-heading"
                  onClick={() => handleSelectDate(date, 'day')}
                >
                  <span>{t(`calendar.weekday.${WEEK_DAYS[(date.getDay() + 6) % 7].toLowerCase()}`)}</span>
                  <strong>{date.getDate()}</strong>
                </button>
              ))}
              {DAY_HOURS.map(hour => (
                <React.Fragment key={hour}>
                  <div className="calendar-week-hour">{String(hour).padStart(2, '0')}:00</div>
                  {weekDays.map(date => (
                    <div
                      key={`${date.toISOString()}-${hour}`}
                      className="calendar-week-slot"
                      onClick={() => handleSelectDate(date, 'day')}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => handleDropOnDate(event, date, hour)}
                      aria-label={`${date.toLocaleDateString(locale)} ${hour}:00`}
                    >
                      {tasksForSlot(date, hour).map(task => renderTaskChip(task))}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}

          {viewMode === 'day' && (
            <div className="calendar-day-view">
              <div className="calendar-day-agenda">
                {DAY_HOURS.map(hour => (
                  <div key={hour} className="calendar-day-row">
                    <span>{String(hour).padStart(2, '0')}:00</span>
                    <div
                      className="calendar-day-task-placeholder"
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => handleDropOnDate(event, selectedDate, hour)}
                    >
                      <button type="button" onClick={() => openCreateTask(getSlotDate(selectedDate, hour))}>
                        {t('calendar.emptySlot')}
                      </button>
                    </div>
                  </div>
                ))}
                <div className="calendar-day-timeline-layer">
                  {tasksForDate(selectedDate).map(renderTimelineTask)}
                </div>
              </div>
              <aside className="calendar-day-detail">
                <h3>{t('calendar.dayDetails')}</h3>
                {tasksForDate(selectedDate).length ? (
                  <div className="calendar-day-detail-tasks">
                    {tasksForDate(selectedDate).map(task => (
                      <button key={task.occurrenceKey} type="button" onClick={() => openEditTask(task, task.startAt, task.parentTaskId, task.occurrenceDate)}>
                        <span style={{ background: task.headerColor || '#2563eb' }} />
                        {task.title}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p>{t('calendar.dayDetailsEmpty')}</p>
                )}
              </aside>
            </div>
          )}
        </section>

        <aside className="calendar-board-sidebar" aria-label={t('calendar.sidebarTitle')}>
          <div className="calendar-sidebar-section">
            <h2>{t('calendar.sidebarTitle')}</h2>
            <p>{t('calendar.sidebarDescription')}</p>
          </div>
          <div className="calendar-stat-list">
            <div className="calendar-stat">
              <FiCheckCircle aria-hidden="true" />
              <div>
                <strong>{completedCount}</strong>
                <span>{t('calendar.stats.completed')}</span>
              </div>
            </div>
            <div className="calendar-stat">
              <FiClock aria-hidden="true" />
              <div>
                <strong>{habitCount}</strong>
                <span>{t('calendar.stats.habits')}</span>
              </div>
            </div>
          </div>
          <div className="calendar-empty-state">
            <strong>{t('calendar.emptyTitle')}</strong>
            <span>{t('calendar.emptyDescription')}</span>
          </div>
        </aside>
      </main>

      <CalendarTaskModal
        calendarId={calendarSpace.id}
        isOpen={isTaskModalOpen}
        task={editingTask}
        seriesTaskId={editingSeriesTaskId}
        draft={taskDraft}
        occurrenceStartAt={editingOccurrenceStartAt}
        occurrenceDate={editingOccurrenceDate}
        tasks={tasks}
        labels={labels}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTask(null);
          setEditingSeriesTaskId(null);
          setEditingOccurrenceStartAt(null);
          setEditingOccurrenceDate(null);
          setTaskDraft(null);
        }}
        onSaved={handleTaskSaved}
        onDeleted={() => {
          loadCalendarData();
          setIsTaskModalOpen(false);
          setEditingTask(null);
          setEditingSeriesTaskId(null);
          setEditingOccurrenceStartAt(null);
          setEditingOccurrenceDate(null);
          setTaskDraft(null);
        }}
        onLabelsChange={setLabels}
      />
    </div>
  );
};
